import { type JSX, type SVGProps } from "react";
import { useRouterState } from "@tanstack/react-router";
import { type components } from "@njbsoft/types/customer";
import { BuildingIcon, ClipboardCheckIcon, HomeIcon, MailIcon, UserIcon } from "@/components/icons";
import { type AppLink } from "./app-link";
import { type Preselect } from "./home-standing";
import { type IssueStatusFilter } from "./letters-search";
import { requestTestSearch } from "./request-test-search";

export { type AppLink } from "./app-link";

type CustomerTestRequest = components["schemas"]["CustomerTestRequest"];

export type DestinationId = "home" | "properties" | "testing" | "letters" | "account";

/** A route a link reaches with no params or required search: a destination's landing page. */
type LandingPath = {
  [P in AppLink["to"]]: { to: P } extends AppLink ? P : never;
}[AppLink["to"]];

export interface Destination {
  id: DestinationId;
  path: LandingPath;
  labelKey: string;
  shortLabelKey: string;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  /** The path prefixes the destination owns, each matched on a segment boundary. */
  matches: readonly string[];
}

export const DESTINATIONS: readonly Destination[] = [
  {
    id: "home",
    path: "/home",
    matches: ["/home"],
    labelKey: "nav.home",
    shortLabelKey: "nav.home",
    icon: HomeIcon,
  },
  {
    id: "properties",
    path: "/properties",
    matches: ["/properties"],
    labelKey: "nav.properties",
    shortLabelKey: "nav.properties",
    icon: BuildingIcon,
  },
  {
    id: "testing",
    path: "/testing/request",
    matches: ["/testing"],
    labelKey: "nav.testing",
    shortLabelKey: "nav.testing",
    icon: ClipboardCheckIcon,
  },
  {
    id: "letters",
    path: "/letters",
    matches: ["/letters"],
    labelKey: "nav.lettersLong",
    shortLabelKey: "nav.letters",
    icon: MailIcon,
  },
  {
    id: "account",
    path: "/account",
    matches: ["/account"],
    labelKey: "nav.account",
    shortLabelKey: "nav.account",
    icon: UserIcon,
  },
];

function ownsPath(prefix: string, pathname: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The destination owning `pathname` by its longest matching prefix, or null. */
export function resolveDestination(pathname: string): DestinationId | null {
  let found: { id: DestinationId; length: number } | null = null;
  for (const destination of DESTINATIONS) {
    for (const prefix of destination.matches) {
      if (ownsPath(prefix, pathname) && (!found || prefix.length > found.length)) {
        found = { id: destination.id, length: prefix.length };
      }
    }
  }
  return found?.id ?? null;
}

/** The destination the current page belongs to, or null when no destination owns its path. */
export function useActiveDestination(): DestinationId | null {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return resolveDestination(pathname);
}

export function homeLink(): AppLink {
  return { to: "/home" };
}

export function propertiesLink(): AppLink {
  return { to: "/properties" };
}

export function propertyLink(facilityId: string): AppLink {
  return { to: "/properties/$facilityId", params: { facilityId } };
}

export function deviceLink(installationId: string): AppLink {
  return { to: "/properties/devices/$installationId", params: { installationId } };
}

/** A test or inspection's record. */
export function submissionLink(submissionId: string): AppLink {
  return { to: "/testing/history/$submissionId", params: { submissionId } };
}

/** Where "All {n}" of the devices needing a test leads: the Devices tab, narrowed to them. */
export function needsTestDevicesLink(): AppLink {
  return { to: "/properties", search: { tab: "devices", status: "needs_test" } };
}

/** The request wizard, opened on the preselected property and devices when there are any. */
export function requestTestLink(preselect: Preselect | null): AppLink {
  return preselect
    ? { to: "/testing/request", search: requestTestSearch(preselect) }
    : { to: "/testing/request" };
}

export function requestsLink(): AppLink {
  return { to: "/testing/requests" };
}

/** A request's record, its conversation included. */
export function requestLink(request: Pick<CustomerTestRequest, "id" | "thread">): AppLink {
  return { to: "/testing/requests/$requestId", params: { requestId: request.id } };
}

/** The Testing destination's past tests. */
export function historyLink(): AppLink {
  return { to: "/testing/history" };
}

export function lettersLink(): AppLink {
  return { to: "/letters" };
}

export function letterLink(noticeId: string): AppLink {
  return { to: "/letters/$noticeId", params: { noticeId } };
}

/** Where a property or device record's "All letters" leads: the letters, narrowed to its property. */
export function propertyLettersLink(facilityId: string): AppLink {
  return { to: "/letters", search: { property: facilityId } };
}

/** The compliance issues, narrowed to `status` when one is named. */
export function issuesLink(status?: IssueStatusFilter): AppLink {
  return status ? { to: "/letters/issues", search: { status } } : { to: "/letters/issues" };
}

/** A compliance issue's record. */
export function issueLink(violationId: string): AppLink {
  return { to: "/letters/issues/$violationId", params: { violationId } };
}

export function accountLink(): AppLink {
  return { to: "/account" };
}
